import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

import { getProject, fetchTicketComments } from "../../../services/projects";

export function useTicketViewData(
  ticketId: string | null,
  options?: { pollingEnabled?: boolean },
) {
  // Track whether we've seen AI comments without citations — once we see
  // citations on all of them, stop the extra polling.
  const citationsLoadingRef = useRef(false);

  const baseRefetchInterval = options?.pollingEnabled ? 5000 : false;
  const refetchInterval =
    baseRefetchInterval || citationsLoadingRef.current ? 5000 : false;

  const ticket = useQuery({
    queryKey: ["ticket-view", ticketId],
    queryFn: () => getProject(ticketId!),
    enabled: !!ticketId,
    staleTime: refetchInterval ? 0 : 60_000,
    refetchInterval,
  });

  const comments = useQuery({
    queryKey: ["ticket-view-comments", ticketId],
    queryFn: () => fetchTicketComments({ ticketId: ticketId!, query: "" }),
    enabled: !!ticketId,
    staleTime: refetchInterval ? 0 : 60_000,
    refetchInterval,
  });

  // Determine if citations are still loading (AI comments exist but lack citations)
  const citationsLoading = useMemo(() => {
    if (!comments.data?.length) return false;
    const aiComments = comments.data.filter((c) => c.isAI && !c.isDeleted);
    if (aiComments.length === 0) return false;
    return aiComments.some(
      (c) =>
        !c.citations?.length ||
        c.citations.some(
          (cit: Record<string, unknown>) => "url" in cit && cit.rel_x == null,
        ),
    );
  }, [comments.data]);

  citationsLoadingRef.current = citationsLoading;

  const ticketData = ticket.data;
  const latestBoard = ticketData?.revisionBoards
    ?.filter((b) => !b.deletedAt)
    ?.at(-1);
  const contextFiles = latestBoard?.contextFiles ?? [];
  const hasContextAssets = contextFiles.length > 0;
  const latestReviewFiles = latestBoard?.reviewFiles ?? [];

  return {
    ticket: ticketData,
    comments: comments.data,
    isLoading: ticket.isLoading || comments.isLoading,
    citationsLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  };
}
